import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  ArrowLeft, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  Search,
  Filter,
  Download,
  FileSpreadsheet,
  FileText,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function History() {
  const { logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchDevice, setSearchDevice] = useState("");
  const [searchPersonnel, setSearchPersonnel] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 50;
  
  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportStatus, setExportStatus] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams();
      if (searchDevice) params.append("device_number", searchDevice);
      if (searchPersonnel) params.append("personnel_number", searchPersonnel);
      if (filterStatus !== "all") params.append("status", filterStatus);
      params.append("limit", limit.toString());
      params.append("skip", (page * limit).toString());

      const response = await axios.get(`${API}/history?${params.toString()}`);
      setHistory(response.data.entries);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching history:", error);
      setLoadError("Die Historie konnte nicht geladen werden. Bitte erneut versuchen.");
      if (error.response?.status === 401) {
        logout();
      }
    }
    setLoading(false);
  }, [searchDevice, searchPersonnel, filterStatus, page, logout]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append("format", exportFormat);
      if (exportStatus !== "all") params.append("status", exportStatus);
      if (exportFromDate) params.append("from_date", new Date(exportFromDate).toISOString());
      if (exportToDate) params.append("to_date", new Date(exportToDate).toISOString());

      const response = await axios.get(`${API}/export/loans?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ausleihhistorie.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export erfolgreich");
      setShowExportModal(false);
    } catch (error) {
      toast.error("Fehler beim Export");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="history-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Ausleihhistorie
                </h1>
                <p className="text-sm text-slate-500">{total} Einträge insgesamt</p>
              </div>
            </div>
            
            <Button onClick={() => setShowExportModal(true)} data-testid="export-btn">
              <Download className="h-4 w-4 mr-2" />
              Exportieren
            </Button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500 mb-1 block">Gerät suchen</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Gerätename oder Seriennummer..."
                    value={searchDevice}
                    onChange={(e) => { setSearchDevice(e.target.value); setPage(0); }}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500 mb-1 block">Personalnummer</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Personalnummer..."
                    value={searchPersonnel}
                    onChange={(e) => { setSearchPersonnel(e.target.value); setPage(0); }}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="w-[180px]">
                <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(0); }}>
                  <SelectTrigger>
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="OPEN">Offen</SelectItem>
                    <SelectItem value="CLOSED">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loadError ? (
              <div className="p-8 text-center space-y-3" role="alert">
                <p>{loadError}</p>
                <Button variant="outline" onClick={fetchHistory}>Erneut versuchen</Button>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gerät</TableHead>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead>Ausgabe</TableHead>
                      <TableHead>Rückgabe</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Schicht</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono font-medium">
                          {entry.handheld_display_name}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{entry.user_name}</div>
                          <div className="text-xs text-slate-500">{entry.user_personal_number}</div>
                        </TableCell>
                        <TableCell className="text-sm">{entry.user_company}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-emerald-600">
                            <ArrowUpFromLine className="h-3 w-3" />
                            <span className="text-xs">{formatDate(entry.start_timestamp)}</span>
                          </div>
                          <div className="text-xs text-slate-500">{entry.issued_by_staff_name}</div>
                        </TableCell>
                        <TableCell>
                          {entry.end_timestamp ? (
                            <>
                              <div className="flex items-center gap-1 text-blue-600">
                                <ArrowDownToLine className="h-3 w-3" />
                                <span className="text-xs">{formatDate(entry.end_timestamp)}</span>
                              </div>
                              <div className="text-xs text-slate-500">{entry.returned_by_staff_name}</div>
                            </>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={entry.status === "OPEN" ? "bg-red-500" : "bg-emerald-500"}>
                            {entry.status === "OPEN" ? "Offen" : "Abgeschlossen"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{entry.shift}</TableCell>
                      </TableRow>
                    ))}
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          Keine Einträge gefunden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-slate-500">
                      Seite {page + 1} von {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        Zurück
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Ausleihhistorie exportieren
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      CSV
                    </div>
                  </SelectItem>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel (.xlsx)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status filtern</Label>
              <Select value={exportStatus} onValueChange={setExportStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="OPEN">Nur offene</SelectItem>
                  <SelectItem value="CLOSED">Nur abgeschlossene</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Von
                </Label>
                <Input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Bis
                </Label>
                <Input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleExport} disabled={exporting}>
              {exporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Exportieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
